<template>
  <div>
    <div
      :class="[
        'relative border-2 border-dashed rounded-2xl overflow-hidden bg-white',
        error ? 'border-red-300 bg-red-50' : 'border-primary-200',
      ]"
    >
      <canvas
        ref="canvasRef"
        class="w-full cursor-crosshair touch-none"
        :style="{ height: '160px' }"
        @mousedown="startDrawing"
        @mousemove="draw"
        @mouseup="stopDrawing"
        @mouseout="stopDrawing"
        @touchstart="startDrawing"
        @touchmove="draw"
        @touchend="stopDrawing"
      ></canvas>
      <div
        v-if="!hasDrawn"
        class="absolute inset-0 pointer-events-none flex flex-col items-center justify-center text-surface-300"
      >
        <i class="pi pi-pen-to-square text-3xl mb-2 opacity-50"></i>
        <span class="text-sm font-medium">חתמו כאן</span>
      </div>
      <button
        v-if="hasDrawn"
        class="absolute bottom-2 left-3 text-xs text-primary-600 font-medium hover:text-primary-800 bg-white/80 px-2 py-1 rounded-md"
        @click.prevent="clear"
      >
        ניקוי
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';

const props = defineProps<{
  modelValue?: string | null;
  error?: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string | null];
}>();

const canvasRef = ref<HTMLCanvasElement | null>(null);
const isDrawing = ref(false);
const hasDrawn = ref(!!props.modelValue);

onMounted(() => {
  if (!canvasRef.value) return;
  const rect = canvasRef.value.getBoundingClientRect();
  canvasRef.value.width = rect.width;
  canvasRef.value.height = rect.height;
  const ctx = canvasRef.value.getContext('2d');
  if (!ctx) return;
  ctx.lineWidth = 2.5;
  ctx.lineCap = 'round';
  ctx.strokeStyle = '#334155';

  if (props.modelValue && props.modelValue.startsWith('data:image')) {
    const img = new Image();
    img.onload = () => ctx.drawImage(img, 0, 0);
    img.src = props.modelValue;
  }
});

const getCoordinates = (e: MouseEvent | TouchEvent) => {
  const rect = canvasRef.value!.getBoundingClientRect();
  const touch = 'touches' in e ? e.touches[0] : e;
  return {
    x: touch.clientX - rect.left,
    y: touch.clientY - rect.top,
  };
};

const startDrawing = (e: MouseEvent | TouchEvent) => {
  e.preventDefault();
  const { x, y } = getCoordinates(e);
  const ctx = canvasRef.value!.getContext('2d')!;
  ctx.beginPath();
  ctx.moveTo(x, y);
  isDrawing.value = true;
};

const draw = (e: MouseEvent | TouchEvent) => {
  e.preventDefault();
  if (!isDrawing.value) return;
  const { x, y } = getCoordinates(e);
  const ctx = canvasRef.value!.getContext('2d')!;
  ctx.lineTo(x, y);
  ctx.stroke();
};

const stopDrawing = () => {
  if (!isDrawing.value) return;
  isDrawing.value = false;
  hasDrawn.value = true;
  emit('update:modelValue', canvasRef.value!.toDataURL('image/png'));
};

const clear = () => {
  const ctx = canvasRef.value!.getContext('2d')!;
  ctx.clearRect(0, 0, canvasRef.value!.width, canvasRef.value!.height);
  hasDrawn.value = false;
  emit('update:modelValue', null);
};
</script>
